import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle, X, Handshake, ArrowRight, Sparkles, Shield, Building2, Users, GraduationCap, Globe, Linkedin, Twitter, Facebook, Instagram, ExternalLink, Zap, Landmark, Leaf } from 'lucide-react';
import axios from 'axios';
import '../forceInputs.css';
import DevenirPartenaireForm from './DevenirPartenaireForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t } = useTranslation();
  const [showContactForm, setShowContactForm] = useState(false);
  const [showDemandeForm, setShowDemandeForm] = useState(false);
  const [showPartenaireForm, setShowPartenaireForm] = useState(false);
  const [formData, setFormData] = useState({ nom: '', prenom: '', email: '', telephone: '', entreprise: '', message: '' });
  const [demandeFormData, setDemandeFormData] = useState({ nom: '', prenom: '', email: '', telephone: '', entreprise: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [demandeLoading, setDemandeLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [demandeSuccess, setDemandeSuccess] = useState(false);
  const [error, setError] = useState('');
  const [demandeError, setDemandeError] = useState('');
  const [email, setEmail] = useState('');
  // V38.05 — Newsletter subscribe state
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMsg, setNewsletterMsg] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);

  const handleNewsletterSubscribe = async (e) => {
    if (e) e.preventDefault();
    const cleaned = (email || '').trim().toLowerCase();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      setNewsletterMsg('Adresse email invalide.');
      return;
    }
    if (!newsletterConsent) {
      setNewsletterMsg('Veuillez accepter la politique de confidentialité.');
      return;
    }
    setNewsletterLoading(true);
    setNewsletterMsg('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, {
        email: cleaned,
        source: 'footer',
        lang: 'fr',
        consent: true,
      });
      if (res.data?.status === 'already_subscribed') {
        setNewsletterMsg('Vous êtes déjà inscrit, merci !');
      } else if (res.data?.status === 'reactivated') {
        setNewsletterMsg('Inscription réactivée. Merci !');
      } else {
        setNewsletterMsg('Inscription confirmée. À très vite !');
      }
      setEmail('');
    } catch (err) {
      setNewsletterMsg(err?.response?.data?.detail || 'Erreur inscription.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSuccess(true);
      setFormData({ nom: '', prenom: '', email: '', telephone: '', entreprise: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi du message';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleDemandeSubmit = async (e) => {
    e.preventDefault();
    setDemandeLoading(true);
    setDemandeError('');
    setDemandeSuccess(false);
    try {
      await axios.post(`${BACKEND_URL}/api/demandes-publiques`, demandeFormData);
      setDemandeSuccess(true);
      setDemandeFormData({ nom: '', prenom: '', email: '', telephone: '', entreprise: '', message: '' });
      setTimeout(() => { setDemandeSuccess(false); setShowDemandeForm(false); }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande';
      setDemandeError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setDemandeLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleDemandeChange = (e) => setDemandeFormData({ ...demandeFormData, [e.target.name]: e.target.value });

  // V38.40 — Footer piloté par les données (géré par le Super Admin).
  // Fallback hardcodé si l'API est indisponible → aucune régression.
  const ICON_BY_HREF = {
    '/qui-sommes-nous': Users, '/services': Zap, '/a-propos': Globe,
    '/garanties/coppam': Shield,
    '/fogedom': Building2, '/fogedom/presentation': Building2, '/formascop': GraduationCap,
  };

  const FALLBACK_QUICK = [
    { label: 'Qui sommes-nous', href: '/qui-sommes-nous' },
    { label: 'Nos services', href: '/services' },
    { label: 'Écosystème O’SCOP', href: '/a-propos' },
    { label: 'COPPAM', href: '/garanties/coppam' },
    { label: 'FOGEDOM-SCIC', href: '/fogedom/presentation' },
    { label: "FORMA'SCOP", href: '/formascop' },
  ];
  const FALLBACK_FTPE = {
    label: 'FTPE Outremer',
    items: [
      { label: 'FTPE Outremer', href: '/ftpe-outremer' },
      { label: 'Notre mission', href: '/ftpe-outremer/mission' },
      { label: 'Nos valeurs', href: '/ftpe-outremer/valeurs' },
      { label: 'FAQ', href: '/ftpe-outremer/faq' },
      { label: 'Charte de partenariat', href: '/ftpe-outremer/charte' },
    ],
  };
  const FALLBACK_LEGAL = [
    { label: 'Mentions légales', href: '/mentions-legales' },
    { label: 'Conditions générales d’utilisation', href: '/conditions-generales' },
                  { label: 'Médiation de la consommation', href: '/conditions-generales#mediation-consommation' },
    { label: 'Politique RGPD', href: '/politique-rgpd' },
  ];

  const [footerCols, setFooterCols] = useState(null); // null = pas encore chargé / API KO → fallback

  useEffect(() => {
    let active = true;
    axios.get(`${BACKEND_URL}/api/public/frontoffice-footer`)
      .then((res) => { if (active) setFooterCols(res.data?.columns || []); })
      .catch(() => { if (active) setFooterCols(null); });
    return () => { active = false; };
  }, []);

  const toLink = (it) => {
    let href = it.to || '#';
    let label = it.label;
    if (!it.external && href === '/a-propos') {
      if (/coppam/i.test(label || '')) {
        href = '/garanties/coppam';
      } else {
        label = 'Écosystème O’SCOP';
      }
    }
    // Les deux présentations restent distinctes, y compris avec un menu enregistré avant la correction.
    if (!it.external && ['/fogedom', '/fogedom/presentation'].includes(href)) {
      href = /scic/i.test(it.label || '') ? '/fogedom/presentation' : '/fogedom';
    }
    return { label, href, external: !!it.external };
  };
  const linkCols = footerCols ? footerCols.filter((c) => c.key !== 'legal') : null;
  const navCol = linkCols && linkCols[0]
    ? { label: linkCols[0].label, items: (linkCols[0].items || []).map(toLink) }
    : { label: 'Navigation', items: FALLBACK_QUICK };
  const ftpeCol = linkCols && linkCols[1]
    ? { label: linkCols[1].label, items: (linkCols[1].items || []).map(toLink) }
    : FALLBACK_FTPE;
  const legalColData = footerCols ? footerCols.find((c) => c.key === 'legal') : null;
  let legalLinks = legalColData ? (legalColData.items || []).map(toLink) : FALLBACK_LEGAL;
  // Garantit la présence du lien Médiation de la consommation (obligation légale)
  const MEDIATION_LINK = { label: 'Médiation de la consommation', href: '/conditions-generales#mediation-consommation' };
  if (!legalLinks.some((l) => l.href === MEDIATION_LINK.href)) {
    legalLinks = [...legalLinks, MEDIATION_LINK];
  }

  const quickLinks = (navCol.items || []).map((l) => ({ ...l, icon: ICON_BY_HREF[l.href] || ArrowRight }));
  const ftpeLinks = ftpeCol.items || [];

  return (
    <footer className="relative bg-slate-950 text-white overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Formulaire de contact - Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{backgroundColor: 'rgba(0, 0, 0, 0.8)'}} onClick={(e) => { if (e.target === e.currentTarget) setShowContactForm(false); }}>
          <div className="bg-slate-900 border border-slate-700/50 py-10 px-6 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowContactForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" aria-label="Fermer"><X className="h-6 w-6" /></button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center"><Mail className="w-8 h-8 text-white" /></div>
              <h2 className="text-2xl font-bold text-white mb-2">{t('footer.contact_us', 'Nous Contacter')}</h2>
              <p className="text-slate-400">{t('forms.contact_question', 'Une question ? Un projet ? N\'hésitez pas à nous écrire.')}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="nom" className="text-slate-300">Nom *</Label><Input id="nom" name="nom" value={formData.nom} onChange={handleChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
                <div><Label htmlFor="prenom" className="text-slate-300">Prénom *</Label><Input id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="email" className="text-slate-300">Email *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
                <div><Label htmlFor="telephone" className="text-slate-300">Téléphone *</Label><Input id="telephone" name="telephone" type="tel" value={formData.telephone} onChange={handleChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              </div>
              <div><Label htmlFor="entreprise" className="text-slate-300">Entreprise</Label><Input id="entreprise" name="entreprise" value={formData.entreprise} onChange={handleChange} className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              <div><Label htmlFor="message" className="text-slate-300">Message *</Label><Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              {success && <Alert className="border-green-500/50 bg-green-500/10"><CheckCircle className="h-4 w-4 text-green-400" /><AlertDescription className="text-green-300">Message envoyé avec succès !</AlertDescription></Alert>}
              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl py-6"><Send className="h-4 w-4 mr-2" />{loading ? 'Envoi...' : 'Envoyer'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Formulaire de demande - Modal */}
      {showDemandeForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{backgroundColor: 'rgba(0, 0, 0, 0.8)'}} onClick={(e) => { if (e.target === e.currentTarget) setShowDemandeForm(false); }}>
          <div className="bg-slate-900 border border-slate-700/50 py-10 px-6 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowDemandeForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" aria-label="Fermer"><X className="h-6 w-6" /></button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center"><Send className="w-8 h-8 text-white" /></div>
              <h2 className="text-2xl font-bold text-white mb-2">Déposer une demande</h2>
              <p className="text-slate-400">Nous vous répondrons dans les plus brefs délais.</p>
            </div>
            <form onSubmit={handleDemandeSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="demande-nom" className="text-slate-300">Nom *</Label><Input id="demande-nom" name="nom" value={demandeFormData.nom} onChange={handleDemandeChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
                <div><Label htmlFor="demande-prenom" className="text-slate-300">Prénom *</Label><Input id="demande-prenom" name="prenom" value={demandeFormData.prenom} onChange={handleDemandeChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="demande-email" className="text-slate-300">Email *</Label><Input id="demande-email" name="email" type="email" value={demandeFormData.email} onChange={handleDemandeChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
                <div><Label htmlFor="demande-telephone" className="text-slate-300">Téléphone *</Label><Input id="demande-telephone" name="telephone" type="tel" value={demandeFormData.telephone} onChange={handleDemandeChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              </div>
              <div><Label htmlFor="demande-entreprise" className="text-slate-300">Entreprise *</Label><Input id="demande-entreprise" name="entreprise" value={demandeFormData.entreprise} onChange={handleDemandeChange} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              <div><Label htmlFor="demande-message" className="text-slate-300">Message *</Label><Textarea id="demande-message" name="message" value={demandeFormData.message} onChange={handleDemandeChange} rows={4} required className="mt-1 bg-slate-800/50 border-slate-700 text-white rounded-xl" /></div>
              {demandeSuccess && <Alert className="border-green-500/50 bg-green-500/10"><CheckCircle className="h-4 w-4 text-green-400" /><AlertDescription className="text-green-300">Demande envoyée avec succès !</AlertDescription></Alert>}
              {demandeError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{demandeError}</AlertDescription></Alert>}
              <Button type="submit" disabled={demandeLoading} className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl py-6"><Send className="h-4 w-4 mr-2" />{demandeLoading ? 'Envoi...' : 'Envoyer'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Newsletter Section */}
      <div className="relative border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-800/30 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left lg:max-w-sm">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Restez informé</h3>
                <p className="text-slate-400">Inscrivez-vous à notre newsletter pour les dernières actualités</p>
              </div>
              <div className="w-full min-w-0 max-w-xl">
                <form className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]" onSubmit={handleNewsletterSubscribe}>
                  <Input type="email" aria-label="Votre adresse e-mail pour les actualités" placeholder="Votre email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="newsletter-footer-email-input" className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl px-4 py-3 w-full min-w-0" />
              <label className="sm:col-span-2 sm:row-start-2 flex items-start gap-2 text-sm leading-relaxed text-slate-300">
                <input type="checkbox" checked={newsletterConsent} onChange={(e) => setNewsletterConsent(e.target.checked)} required className="mt-1 h-4 w-4 rounded border-slate-500" />
                <span>J'accepte de recevoir les actualités d'O'SCOP. Désinscription possible à tout moment. <Link to="/politique-rgpd" className="underline hover:text-white">Politique RGPD</Link>.</span>
              </label>
                  <Button type="submit" disabled={newsletterLoading || !newsletterConsent} data-testid="newsletter-footer-submit-btn" className="sm:col-start-2 sm:row-start-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl px-6">{newsletterLoading ? 'Inscription…' : "S'inscrire"} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </form>
              </div>
            </div>
            {newsletterMsg && (<p className="mt-3 text-sm text-amber-300" data-testid="newsletter-footer-message">{newsletterMsg}</p>)}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 xl:grid-cols-4">
            {/* Brand */}
            <div className="min-w-0">
              <Link to="/" className="inline-flex max-w-full items-center justify-center rounded-2xl bg-white p-4 mb-6"><img src="/oscop-logo.png" alt="OBJECTIF SCOP OUTREMER" className="h-20 w-auto max-w-full object-contain" loading="lazy" /></Link>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed"><strong className="text-white">SCIC SAS OBJECTIF SCOP OUTREMER</strong><br />L'innovation coopérative au service des Outre-mer.</p>
              <div className="flex gap-3">
                {[{ icon: Linkedin, href: '#' }, { icon: Twitter, href: '#' }, { icon: Facebook, href: '#' }, { icon: Instagram, href: '#' }].filter((social) => social.href && social.href !== '#').map((social, i) => (
                  <a key={i} href={social.href} className="w-10 h-10 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 flex items-center justify-center text-slate-400 hover:text-white transition-all"><social.icon className="w-4 h-4" /></a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-400" /> {navCol.label}</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-400 hover:text-white transition-all group"><div className="w-8 h-8 flex-shrink-0 rounded-lg bg-slate-800/50 group-hover:bg-slate-700/50 flex items-center justify-center transition-all"><link.icon className="w-4 h-4" /></div><span className="min-w-0 break-words">{link.label}</span></a>
                    ) : (
                      <Link to={link.href} className="flex items-center gap-3 text-slate-400 hover:text-white transition-all group"><div className="w-8 h-8 flex-shrink-0 rounded-lg bg-slate-800/50 group-hover:bg-slate-700/50 flex items-center justify-center transition-all"><link.icon className="w-4 h-4" /></div><span className="min-w-0 break-words">{link.label}</span></Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* FTPE Outremer Links */}
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Landmark className="w-5 h-5 text-green-400" /> {ftpeCol.label}</h4>
              <ul className="space-y-3">
                {ftpeLinks.map((link, i) => (
                  <li key={i}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-all text-sm">{link.label}</a>
                    ) : (
                      <Link to={link.href} className="text-slate-400 hover:text-white transition-all text-sm">{link.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* COPPAM (déplacé depuis le header) */}
            <div data-testid="footer-coppam-col" className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-400" /> COPPAM</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Présentation de COPPAM', to: '/garanties/coppam' },
                  { label: 'Nos engagements ESS', to: '/engagements-ess' },
                  { label: 'Fonctionnement', to: '/fonctionnement-dispositif' },
                  { label: 'Écosystème O’SCOP', to: '/a-propos' },
                  { label: 'Garanties solidaires', to: '/garanties-solidaires-btp' },
                  { label: 'Innovation sociale', to: '/innovation-sociale' },
                  { label: 'Offre Adhérent COPPAM', to: '/offre-coppam' },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.to} className="text-slate-400 hover:text-white transition-all text-sm">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* FOGEDOM-SCIC (déplacé depuis le header) */}
            <div data-testid="footer-fogedom-col" className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-400" /> Entraide FOGEDOM</h4>
              <ul className="space-y-3">
                {[
                  { label: 'F.O.G.E.D.O.M — Présentation', to: '/fogedom' },
                  { label: 'FOGEDOM-SCIC — Fonds interne', to: '/fogedom/presentation' },
                  { label: 'Accompagnement', to: '/fogedom/accompagnement' },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.to} className="text-slate-400 hover:text-white transition-all text-sm">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Bâtiment (visibilité BatiSCOP Construction bois) */}
            <div data-testid="footer-batiment-col" className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Leaf className="w-5 h-5 text-green-400" /> Bâtiment</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/batiment-expertise" className="text-slate-400 hover:text-white transition-all text-sm">Bâtiment & Expertise Civile</Link>
                </li>
                <li>
                  <a href="https://batiscop.oscop.fr" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-all text-sm flex items-center gap-1" data-testid="footer-batiscop-link">
                    Construction bois — BatiSCOP <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Mail className="w-5 h-5 text-amber-400" /> Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-slate-400"><MapPin className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" /><span className="text-sm">13 rue Rodrigue YOUYOUTE<br />97139 Les Abymes<br />Guadeloupe (FRANCE)</span></li>
                <li><a href="tel:+590590460521" className="flex items-start gap-3 text-slate-400 hover:text-white transition-all"><Phone className="w-5 h-5 flex-shrink-0 text-amber-400" /><span className="text-sm">+590 590 46 05 21</span></a></li>
                <li><a href="mailto:contact@objectifscopoutremer.com" className="flex items-start gap-3 text-slate-400 hover:text-white transition-all"><Mail className="w-5 h-5 flex-shrink-0 text-amber-400" /><span className="min-w-0 break-words text-sm">contact@objectifscopoutremer.com</span></a></li>
                <li><a href="https://www.objectifscopoutremer.com" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-slate-400 hover:text-white transition-all"><Globe className="w-5 h-5 flex-shrink-0 text-amber-400" /><span className="min-w-0 break-words text-sm">www.objectifscopoutremer.com</span></a></li>
              </ul>
            </div>

            {/* Actions */}
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" /> Actions</h4>
              <div className="space-y-3">
                <button onClick={() => setShowContactForm(true)} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl px-6 py-3 transition-all"><Mail className="w-4 h-4" /> Nous contacter</button>
                <button onClick={() => window.location.assign('/demande/deposer')} className="w-full flex items-center justify-center gap-2 bg-slate-800/50 hover:bg-slate-700/50 text-white font-medium border border-slate-700/50 rounded-xl px-6 py-3 transition-all" data-testid="footer-deposer-demande-btn"><Send className="w-4 h-4" /> Déposer une demande</button>
                <button onClick={() => setShowPartenaireForm(true)} className="w-full flex items-center justify-center gap-2 bg-slate-800/50 hover:bg-slate-700/50 text-white font-medium border border-slate-700/50 rounded-xl px-6 py-3 transition-all"><Handshake className="w-4 h-4" /> Devenir Partenaire</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire Devenir Partenaire */}
      <DevenirPartenaireForm open={showPartenaireForm} onOpenChange={setShowPartenaireForm} />

      {/* Bottom Bar */}
      <div className="relative border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
              {legalLinks.map((link, i) => (
                link.external
                  ? <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-all">{link.label}</a>
                  : <Link key={i} to={link.href} className="hover:text-white transition-all">{link.label}</Link>
              ))}
              <Link to="/admin/login" className="text-amber-500 hover:text-amber-400 font-medium flex items-center gap-1">Admin <ExternalLink className="w-3 h-3" /></Link>
            </div>
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} SCIC SAS OBJECTIF SCOP OUTREMER. Tous droits réservés.</p>
          </div>
          <div className="flex justify-center mt-8"><img src="https://customer-assets.emergentagent.com/job_oscop-admin/artifacts/cj6emt6o_logo_CRESS_Guadeloupe.jpg" alt="CRESS Guadeloupe" className="h-16 object-contain bg-white rounded-xl p-2" /></div>
          <p className="text-center text-xs text-slate-600 mt-4">Économie Sociale et Solidaire | Loi ESS 2014 | Code civil</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
